// Single source of truth for an Ongoing's "story steps", used by BOTH the middle (StoryView, clean)
// and the right side panel (GlobalTimelinePanel, expandable details). Keys are stable so the middle
// can tell the side panel which step to expand/scroll to. Every step has an icon (svg name + color).

import { originMeta } from '@/lib/ongoingOrigin'
import { itemInPack } from '@/lib/packCompare'
import { parseUtc } from '@/lib/dueStatus'
import { timelineDateTime } from '@/lib/timelineTime'
import { eventLabel } from '@/lib/eventTime'
import { toPlainText } from '@/lib/plainText'
import { typeTitle } from '@/lib/globalTimelineFeed'
import { moduleColor } from '@/lib/moduleColors'
import { moduleIcon } from '@/lib/moduleIcons'
import { storyThreadNodes, threadKind, threadOpensEntity } from '@/lib/storyThreads'
import { viaTag, dedupeSteps, isUndrawnStep } from '@/lib/storyVisibility'
import { isEmailResearchItem } from '@/lib/researchEmailItem'
import { messageMeta, threadSource } from '@/lib/sources'
import { senderAddress, senderDisplayName } from '@/lib/senderDisplay'
import { storyDocStep, threadFileRow } from '@/lib/attachmentRows'

// Timeline event kinds that become their own milestone step. The kinds that ARE workspace modules
// take BOTH halves of their look from the one module pair — the colour from @/lib/moduleColors
// (Figma 40000803:38778) and the glyph from @/lib/moduleIcons (40008716-284885) — rather than
// naming a hex or an icon name here. The colour was unified first; the glyph names went on
// drifting (busy-my-web#212): a note was `edit` on the global feed but `notes` on this rail, an
// event `calendar-2` there and `events` here, a contact `user` here and `contacts` everywhere else,
// so the same module wore two shapes depending on which rail you were looking at. Both rails read
// the same registry (@/lib/icons/registry, via EmailSvg and SVGRepository alike), so the canonical
// names resolve identically on either side. The rest are story vocabulary, not modules, and keep
// their own accents.
const KIND_STEP = {
  task: { svg: moduleIcon('task'), color: moduleColor('task'), who: 'added a task' },
  note: { svg: moduleIcon('note'), color: moduleColor('note'), who: 'added a note' },
  event: { svg: moduleIcon('event'), color: moduleColor('event'), who: 'added an event' },
  // Figma Stage 13 "Contact created". Contact is the palette's sixth module (#210) — it used to
  // borrow the event purple here, so a contact and an event read the same on the story rail.
  contact: { svg: moduleIcon('contact'), color: moduleColor('contact'), who: 'added a contact' },
  // The comparison's glyph is the CHAT bubbles, not the note page: frame 40008216:411421 draws the
  // "Comparison created" story entry with vuesax's messages-3 (Pouria, 2026-08-18, pointing at
  // 40008216:411447 directly: "the icon for comparison should be like [Figma]") — two overlapping
  // bubbles, not the app's single-bubble `message-text` this used to borrow. It shared `notes` with
  // a snippet before that, so a comparison and a note read the same on a rail where they mean
  // opposite things — one is something you wrote, the other something you asked. The accent stays
  // the app's comparison blue: the frames give this row no colour.
  comparison: { svg: 'messages-3', color: '#0ea5e9', who: 'compared options' },
  // An ongoing made out of email groups writes ONE of these: "Ongoing created: Health Issues"
  // (busy-my-web#104, frame 40007198:182698). Without a meta here the step was dropped from the
  // middle entirely, so a story created from email opened with nothing on it until the first
  // linked email node — the very step that says where it came from.
  email: { svg: moduleIcon('email'), color: moduleColor('email'), who: 'connected email groups' },
  // The Gmail deletion sync's receipt (busy-my-web#16): "Email deleted from Gmail" /
  // "3 emails deleted from Gmail", the subjects behind the expander. Its OWN kind rather than the
  // `email` one above, because an `email` step means the story is CONNECTED to that mail — and the
  // rule at the bottom of this file drops an email story's origin header as soon as it sees one.
  // A deletion is the opposite fact; it must not stand in for a connection. The bin glyph and the
  // muted grey say "something left" without borrowing the live email indigo.
  'email-deleted': { svg: 'trash', color: '#9ca3af', who: 'deleted email from Gmail' },
  // The parent's permanent receipt for a sub-ongoing it spawned (busy-timeline-srv#12):
  // "Sub-ongoing created: Prada". Without a meta here the step was DROPPED from both rails by the
  // `if (!meta) return` below — the story would have gained a child and said nothing about it. The
  // story glyph and the research blue are the same pair the Briefing's "N sub-ongoings" row wears,
  // so a sub-ongoing looks the same wherever it is written.
  ongoing: { svg: 'story', color: moduleColor('research'), who: 'started a sub-ongoing' },
  conclusion: { svg: 'tick-circle', color: '#00bd62', who: 'drew a conclusion' },
  confirm: { svg: 'tick-circle', color: '#22c55e', who: 'confirmed a step' },
  complete: { svg: 'tick-circle', color: '#22c55e', who: 'completed the story' },
  // The story's life after a task goes overdue (busy-my-web#409, Home page steps 9–13): "Task
  // overdue" in the token sheet's overdue red, Busy's "Next step" question in the primary blue,
  // the purchase in the task green, the receipt on its own glyph, the warranty reminder in the
  // sheet's `state/reminder` amber. Without a meta here every one of them would be dropped by the
  // `if (!meta) return` below and the story would ask its question nowhere. (`receipt` also gives
  // the box's older "Receipt uploaded" chip events a step for the first time.)
  overdue: { svg: 'overdue', color: '#ff0000', who: 'went overdue' },
  question: { svg: 'message-text', color: '#3741f3', who: 'asked a question' },
  purchase: { svg: 'tick-circle', color: moduleColor('task'), who: 'marked it purchased' },
  receipt: { svg: 'receipt-text', color: moduleColor('task'), who: 'saved a receipt' },
  reminder: { svg: 'reminder', color: '#eab308', who: 'set a reminder' },
}

// The kinds whose detail IS the step (drawn on the line under the verb, not behind an expander):
// a contact's summary, an email story's "Created from 2 connected email groups", the sub-ongoing
// receipt — and the lifecycle steps of busy-my-web#409, whose one sentence ("Did you purchase the
// lock?") is the whole point of the row.
const INLINE_DETAIL_KINDS = new Set(['contact', 'email', 'ongoing', 'overdue', 'question', 'purchase', 'receipt', 'reminder'])

// The KIND word a timeline row shows on its small second line, where the app HAS one for that kind
// ("Task" / "Note" / "Event" / "Web search" — the same vocabulary the global feed uses, via
// typeTitle). A contact, a comparison or a conclusion has no noun in it, and those rows show no
// kind line rather than an invented one.
const KIND_WORD = { task: 'task', note: 'note', event: 'event' }
export function kindWord(kind) {
  return KIND_WORD[kind] ? typeTitle(KIND_WORD[kind]) : ''
}

// The three step kinds that ARE an entity of their own — the same three `editOf` builds a SmartBox
// opener for, and the same three `globalTimelineFeed`'s ENTITY_MENU names. One vocabulary, so a task
// is a task on every rail. The middle sizes these steps to their own text (busy-my-web#133).
export const ENTITY_STEP_KINDS = ['task', 'note', 'event']
export function isEntityStep(step) {
  return ENTITY_STEP_KINDS.includes(step?.stepKind)
}

/**
 * Does the middle draw the kind/verb line UNDER a step's own title? (busy-my-web#134)
 *
 * A task step used to render as TWO texts — "Visit Prada store" on line 1 and "Task created" on
 * line 2 — which is what Saffet asked to be "1 text so not two". For a task, a note and an event the
 * title already names the thing and the kind is carried by the row's icon + colour (KIND_STEP), so
 * the verb line is redundant and goes. Everything else keeps its second line: a pack node's
 * "Research Pack created" is the Figma step, and an "Ongoing created" / "Comparison created" row has
 * no icon vocabulary that says it instead.
 *
 * `subTitle` itself is NOT dropped from the step data — the side panel reads it as a row's type
 * label, where it is the only place the kind is written.
 */
export function stepKindLineVisible(step) {
  return !!step?.subTitle && !isEntityStep(step)
}

const isResearchOrigin = (origin) => !origin || ['research', 'web'].includes(String(origin).toLowerCase())

// ── The sub-ongoing receipt (busy-timeline-srv#12) ──────────────────────────────────────────────
// The verb the backend writes on the parent's permanent step: `Sub-ongoing created: <name>`. It is
// matched here rather than assumed of every `ongoing` event, so a future timeline event of that kind
// meaning something else is still drawn — as itself, not as a sub-ongoing.
export const SUB_RECEIPT_VERB = 'Sub-ongoing created'

/** The child's NAME out of a receipt event, or '' when this event is not one. */
export function subReceiptName(event) {
  if (!event || event.kind !== 'ongoing') return ''
  const label = String(event.label || '')
  const sep = label.indexOf(': ')
  return sep > 0 && label.slice(0, sep).trim() === SUB_RECEIPT_VERB ? label.slice(sep + 2).trim() : ''
}

/**
 * A sub-ongoing's own opening line, when this story is one and the backend wrote it.
 * '' for a top-level story, and '' for a child whose story text is empty — the caller falls back to
 * the ordinary origin sentence rather than printing nothing.
 */
export function subOriginLine(story) {
  if (!story?.parentOngoingId) return ''
  return String(story.story || '').trim()
}

// A sub-story's label shown everywhere: just its own title (no prefix).
export function subStoryLabel(sub) {
  if (!sub) return ''
  return (sub.title || '').trim() || 'Sub-story'
}

function originHeadline(s) {
  switch ((s.origin || 'research').toLowerCase()) {
    case 'email': return 'Started from your email'
    case 'task': return 'Started from a task'
    case 'note': return 'Started from a note'
    case 'event': return 'Started from an event'
    default: return `You researched ${(s.title || '').toLowerCase()}`
  }
}

/**
 * The buckets a pack's saved items fall into. One factory, because three places used to write the
 * same object literal and the day a SIXTH bucket appeared (`emails`, busy-timeline-srv#35) two of
 * them would have kept the five.
 */
/**
 * Which saved items a PACK NODE is made of — the rule behind every "N items" / "3 photos 2
 * snippets" the timeline prints for a pack (2026-09-15 parity audit E-1, busy-my-web#485).
 *
 * Two kinds are a pack's, but never a pack NODE's:
 *  - `doc` — the pack's own write-up document, written FROM the items (internal, never a count);
 *  - `comparison` — the answer to a question asked OF the packs. It is a story step of its own
 *    ("<a> vs <b> / Comparison created", frame 40008216:411421); the pack nodes beside it read
 *    "2 photos 2 snippets" and say nothing of it. Counting it inside the packs it was saved into
 *    made the pack card's "N items" jump on save and, worse, a comparison shared by two packs
 *    (no owning `packId`) was grouped as orphaned research into a phantom pseudo-pack.
 * The pack-level BRIEFING still counts comparisons (lib/briefings/pack.js `countPack`) — the
 * pack page's rows are the one place the frames draw "1 comparison" under a pack.
 */
export function isPackNodeItem(r) {
  return !!r && r.kind !== 'doc' && r.kind !== 'comparison'
}

/** Is this item in ANY of these packs — filed into one (`packId`) or shared into some (`packIds`)? */
const itemInAnyPack = (r, packIds) =>
  (!!r.packId && packIds.has(r.packId)) || (r.packIds || []).some(id => packIds.has(id))

export function emptyResearchGroup(query) {
  return { query, photos: [], links: [], notes: [], comps: [], concl: [], emails: [], videos: [] }
}

/**
 * File ONE saved item into its bucket.
 *
 * `notes` is still the fallback — a snippet, a receipt and anything a future save invents read as
 * text, and the note card is the only one that can draw an unknown — but an EMAIL is taken out
 * first: it has a thread behind it, so it renders as something to OPEN rather than as a snippet
 * whose body happens to be a sender's name; a VIDEO is taken out for the same reason a photo is
 * (busy-my-web#546, Pouria: "when we select videos it should calculate as videos not extractions")
 * — it has its own card (Workboard.vue already draws it with its own red play glyph, never the
 * generic document icon a snippet gets), so falling through to `notes` counted it as an Extraction
 * on the pack node's "3 photos 2 Extractions" line even though nothing about it was text.
 */
export function fileResearchItem(group, r) {
  if (r.kind === 'photo') group.photos.push(r)
  else if (r.kind === 'video') group.videos.push(r)
  else if (r.kind === 'link') group.links.push(r)
  else if (r.kind === 'comparison') group.comps.push(r)
  else if (r.kind === 'conclusion') group.concl.push(r)
  else if (isEmailResearchItem(r)) group.emails.push(r)
  else group.notes.push(r)
  return group
}

// Saved items grouped per research STEP (the search query) — each keeps its own files/notes/photos.
function groupResearch(research) {
  const groups = new Map()
  for (const r of research || []) {
    const key = (r.query && r.query.trim()) || 'Saved research'
    if (!groups.has(key)) groups.set(key, emptyResearchGroup(key))
    fileResearchItem(groups.get(key), r)
  }
  return [...groups.values()]
}

/**
 * The LEGACY research groups — material saved before Research Packs existed, or filed against a
 * pack the story no longer carries. `buildStorySteps` draws one pseudo-pack node per group, so
 * they are part of what the timeline calls a pack.
 *
 * Pulled out of `buildStorySteps` so the BRIEFING can ask the same question (busy-my-web#156):
 * the strip counted `story.packs` alone while the rail also drew these, which is how an ongoing
 * showed three packs on the timeline and no research row at all in the Briefing — a count of 0
 * drops the row (`row()` in lib/briefings/story.js), and a story whose only content is legacy
 * research then has no rows left, so the whole strip returns null. "I have three research packs
 * but nothing in the Briefing."
 */
export function legacyPackGroups(story) {
  const packIds = new Set((story?.packs || []).map(p => p.id))
  const research = (story?.research || []).filter(isPackNodeItem)
  // Owned by a real pack in EITHER way an item can be — filed (`packId`) or shared (`packIds`).
  // The test used to read `packId` alone, so a SHARED item (a comparison saved into two packs
  // has no owning `packId` at all, only junction `packIds`) counted as orphaned research and was
  // drawn as a pseudo-pack of its own — the phantom "Research Pack created" step with no counts,
  // stamped now and sorted to the top of the rail (2026-09-15 parity audit E-1, busy-my-web#485).
  // `isPackNodeItem` above already keeps a comparison out of the pack nodes altogether, so this
  // second guard is for any future shared kind.
  return groupResearch(research.filter(r => !itemInAnyPack(r, packIds)))
}

/**
 * Every Research Pack the story's TIMELINE shows: the real ones, then the legacy pseudo-packs.
 *
 * ONE list, so "how many packs does this ongoing have?" has a single answer wherever it is asked.
 * Legacy entries carry no `id` — they are unopenable by design (there is no record behind them),
 * which is why the Briefing's pack LIST still filters on `id` while its COUNT reads this.
 */
export function storyPacks(story) {
  // One entry per PACK, not per pack EVENT: the BFF's story can carry the same pack twice when it
  // was updated after creation, which made "4 packs, one updated" count as 5 (Pouria, 2026-08-26).
  // Identity is the pack id; a legacy entry has none, so its title stands in.
  const seen = new Set()
  const packs = (story?.packs || []).filter(Boolean).filter((p) => {
    const key = p.id || `t:${(p.title || '').toLowerCase()}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
  const titles = new Set(packs.map((p) => (p.title || '').toLowerCase()))
  const legacy = legacyPackGroups(story)
    .map((g, i) => ({
      id: null,
      key: `pack-x:${i}`,
      title: g.query === 'Saved research' ? (story?.title || 'Your research') : g.query,
      legacy: true,
    }))
    .filter((g) => !titles.has((g.title || '').toLowerCase()))
  return [...packs, ...legacy]
}

export function stepSummary(g) {
  const parts = []
  if (g.links.length) parts.push(`${g.links.length} link${g.links.length === 1 ? '' : 's'}`)
  if (g.photos.length) parts.push(`${g.photos.length} photo${g.photos.length === 1 ? '' : 's'}`)
  // Videos count on their own line, same as photos (busy-my-web#546) — `|| []` because a group
  // built before this bucket existed (a caller's own literal, same reasoning as `emails` below) has
  // no `videos` key.
  const videos = g.videos || []
  if (videos.length) parts.push(`${videos.length} video${videos.length === 1 ? '' : 's'}`)
  if (g.notes.length) parts.push(`${g.notes.length} Extraction${g.notes.length === 1 ? '' : 's'}`)
  const comps = g.comps || []
  if (comps.length) parts.push(`${comps.length} comparison${comps.length === 1 ? '' : 's'}`)
  // Saved emails count too (busy-timeline-srv#35) — a pack holding two threads and nothing else has
  // research in it, and a summary that says nothing would read as an empty save. `|| []` because a
  // group built before this bucket existed (a caller's own literal) has no `emails` key.
  const emails = g.emails || []
  if (emails.length) parts.push(`${emails.length} email${emails.length === 1 ? '' : 's'}`)
  // SPACE-separated, no commas: the story step's detail line reads "3 photos 2 snippets" in frame
  // 40007883:371201 (and in the right rail beside it). The counts are a set of small facts, not a
  // sentence — the commas made them read as one, and they are the only punctuation on the line.
  return parts.join(' ')
}

// "tomorrow at 9:00am" / "today at 2:30pm" / "Mon, Jul 12 at 9:00am" — the story's due phrasing.
export function friendlyDue(d) {
  const dt = parseUtc(d)
  if (isNaN(dt.getTime())) return ''
  let h = dt.getHours()
  const ampm = h >= 12 ? 'pm' : 'am'
  h = h % 12 || 12
  const time = `${h}:${String(dt.getMinutes()).padStart(2, '0')}${ampm}`
  const dayStart = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
  const diff = Math.round((dayStart(dt) - dayStart(new Date())) / 86400000)
  if (diff === 0) return `today at ${time}`
  if (diff === 1) return `tomorrow at ${time}`
  if (diff === -1) return `yesterday at ${time}`
  return `${dt.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })} at ${time}`
}

// Timeline stamp, Figma style (component 40007495:315886 / 40007893:378129): TWO stacked,
// right-aligned lines — the date ("18 Aug") over the 12-hour clock ("3:50 pm") — for EVERY step,
// whatever its age. There is no "today,"/"yesterday," wording on the rail (Pouria: "we dont have
// today or yesterday like this on global right hand side" — a mixed rail reading "16 Aug" next to
// "today,15:31" is exactly the drift the design doesn't have). Relative day words stay where the
// design actually draws them — the pack header's "Created today, 4pm" is a different surface — the
// rail always names the day. Delegates to the one shared formatter so this can never drift from the
// global feed / TimelineDateTime again.
const stamp = (d) => timelineDateTime(d)

/**
 * The INSTANT a step sits at on the rail — the sort key behind every `_ts` below (issue #113).
 *
 * It has to be read with the very same parser the stamp above prints with (`parseUtc`), and that is
 * the whole point of this existing: a bare `new Date('2026-08-19T12:00:00')` reads a zone-less
 * timestamp as LOCAL, while `parseUtc` (and therefore the stamp beside the step) reads it as UTC.
 * The BFF sends both shapes for the same field — a stored `DateTime` comes back zone-less, one that
 * never left memory comes back with its `Z` — so ordering by `new Date` sorted the two kinds of
 * timestamp on two different clocks, hours apart. That is what Saffet saw: a task he created AFTER
 * saving snippets/photos sat above the save, because one of the two had been shifted by the local
 * offset. Same parser for the position as for the printed time, and a step can only be where its
 * own stamp says it is.
 *
 * Undated / unparseable steps sort to 0 (the top of an ascending rail) rather than to NaN, which
 * would make the comparison meaningless for every pair it touched.
 */
export function stepTs(value) {
  if (value === null || value === undefined || value === '') return 0
  const t = parseUtc(value).getTime()
  return Number.isFinite(t) ? t : 0
}

// Step shape: { key, svg, color, eyebrow?, title, sub, expandable, group?, detail?, subId? }
// `children` (optional) are sub-ongoings of this story — each is woven into the timeline at the moment
// it was created, so "started a sub-story" reads chronologically alongside saves/tasks/notes.
// The pack a step belongs to. Only a pack's LATEST node carries `packId` (it is the continue
// target) — the earlier created/updated nodes are recognised by their `pack:{id}:{k}` keys, and
// opening the pack must work from EVERY one of its nodes (Pouria, 2026-08-14: "from the root,
// where I created, as well"). Legacy pseudo-packs (`pack-x:…`) stay unopenable by design.
export const packIdOfStep = (s) => s?.packId || (String(s?.key || '').match(/^pack:([^:]+)/)?.[1] ?? null)

/**
 * WHAT THE ONGOING'S TIMELINE IS — the one list the middle and the right-hand rail both draw.
 *
 * Pouria (2026-08-17): "what you show on global timeline is same in story … but 2 extra shows in
 * middle." The two panes were each deciding for themselves what belonged: the rail dropped
 * `filterOnly` steps and the research header once a pack node existed, while the middle kept
 * whatever carried `progress`. Two rules for one question is a drift waiting to happen, and it had
 * already happened — so there is now ONE rule, the rail's (the pane Pouria reads as correct,
 * because it is the same list the Global timeline shows), and the middle reads it too. They cannot
 * disagree again, whatever the story is made of.
 *
 * The rule itself is unchanged from the rail's:
 *  - "Next step" is a to-do the middle used to trail, not something that happened;
 *  - `filterOnly` steps live INSIDE a Research Pack (its notes, its comparisons) — they surface
 *    only when a kind filter asks for them;
 *  - the "Research / Started researching …" header is dropped once a pack node exists, which says
 *    the same thing better (Figma Stage 5) — UNLESS the pack has a name of its own that differs
 *    from the story's (see `keepWithGroups` below).
 */
export function timelineSteps(steps) {
  const all = (steps || []).filter(s => s && s.key !== 'next' && !s.filterOnly)
  if (!all.some(s => s.group)) return all
  // The origin header used to go entirely once pack nodes existed ("the pack nodes carry the
  // story"). A RESEARCH origin keeps it (2026-09-15 parity audit E-5, busy-my-web#487): every
  // research frame opens on "Buy sunglasses / Research started" with the packs under it, and
  // without it the story began mid-sentence, at a pack, with nothing saying what was being
  // researched — the pack there is named "Prada", a narrower thing than the story's own
  // "Buy sunglasses", so the two rows say different things.
  //
  // But when the FIRST pack carries the exact same name as the origin (busy-my-web#546, Pouria in
  // Slack triage: "one thing is extra on story and global timeline, which is search started, we
  // dont need it") — the ordinary case of a single research session whose pack was never renamed
  // off the query that made it — "Research started" and "Research Pack created" are the same
  // sentence twice, the pack's own title line already saying what was searched. Conceptually the
  // same shape as busy-my-web#545's `duePassed` fix in globalTimelineFeed.js (drop a lifecycle-style
  // row once an entity row already states the same fact), applied here to a DIFFERENT list (the
  // story's own steps, not the Global timeline feed) and a DIFFERENT pairing (origin vs. first pack
  // node, matched by name rather than by id — the origin has no packId of its own to match on).
  const origin = all.find(s => s.key === 'origin')
  const firstPack = all.find(s => s.group)
  const sameSubject = !!origin && !!firstPack
    && String(origin.title || '').trim().toLowerCase() === String(firstPack.title || '').trim().toLowerCase()
  return all.filter(s => s.key !== 'origin' || (s.keepWithGroups && !sameSubject))
}

// How many steps the ongoing's middle timeline rests on — the newest four (Pouria, 2026-08-17).
// The four are shown by giving the list the height of ITS OWN last four rows (StoryView measures
// them through lib/timelineCap), never by cutting the list: the earlier steps stay in the page, a
// scroll up away, which is how the right-hand timeline has always worked.
export const AT_REST_STEPS = 4

/**
 * THE GROUPS UNDER AN EMAIL-BORN "Ongoing created" STEP (busy-my-web#499, 2026-09-15 parity audit
 * F2-5; frames 40007198:182698 / 40008461:190811).
 *
 * DECISION TAKEN (2026-09-15): a story made out of email groups shows ONE created step —
 * "Ongoing created / Created from N connected email groups" — with "Connected / <group> · N emails"
 * rows under it, and NOT one "Email received" node per message. The rail used to draw both, so an
 * ongoing born from two groups of 733 mails opened as a wall of "Email received" and the sentence
 * that says where it came from scrolled away.
 *
 * timeline-srv now carries the groups on the created step itself (`groups:[{ name, emailAddress,
 * emailCount }]` — MERGED-NOTES #43). Where the payload has none (an older story, or a BFF that has
 * not shipped the passthrough yet) the same rows are derived CLIENT-SIDE from the story's own
 * threads, grouped by sender — so the step reads the same either way.
 */
export function createdStepGroups(step, story) {
  const fromPayload = (step?.groups || [])
    .filter(g => g && (g.name || g.emailAddress))
    .map(g => ({
      name: String(g.name || g.emailAddress || '').trim(),
      emailAddress: String(g.emailAddress || '').trim(),
      count: Number(g.emailCount ?? g.count) || 0,
    }))
  if (fromPayload.length) return fromPayload
  const byName = new Map()
  for (const em of (story?.emails || [])) {
    if (!em || threadKind(em) !== 'email') continue
    const address = senderAddress(em.senders || em.from || '')
    const name = senderDisplayName(em.senders || em.from || '') || address
    const key = (address || name).toLowerCase()
    if (!key) continue
    const row = byName.get(key) || { name, emailAddress: address, count: 0 }
    row.count += 1
    byName.set(key, row)
  }
  return [...byName.values()]
}

/** "Connected / Vodafone · 12 emails" — one row per group, as the frames draw them. */
export function createdGroupRows(groups) {
  return (groups || []).map(g => ({
    id: `group:${g.emailAddress || g.name}`,
    icon: moduleIcon('email'),
    color: moduleColor('email'),
    label: 'Connected',
    name: g.name || g.emailAddress,
    detail: `${g.name || g.emailAddress} · ${g.count} email${g.count === 1 ? '' : 's'}`,
    count: g.count,
  }))
}

export function buildStorySteps(story, children = []) {
  if (!story) return []
  const origin = originMeta(story.origin)
  // The timeline (search / save / task / note / …) IS the chronological record — walk it in time order.
  //
  // ONE OBJECT, ONE STEP (spec §5). A parent's payload now carries its descendants' steps as well as
  // its own, so the same task can arrive twice: once because the child announced it and once because
  // the parent did. The story's OWN steps win — they are the ones tied to this page's packs — so the
  // dedupe is run over own-first and the list is put back in time order afterwards. The server dedupes
  // too; this is the same rule on the side that assembles the page, for a payload merged from more
  // than one read (a story reloaded on `ongoing-advanced` while another was in flight).
  // …and without the two lifecycle steps no timeline draws any more (Pouria, 2026-10-01): "Task
  // overdue" and "Next step" stay in the payload as storyLifecycle's markers, but the task row
  // already says it is overdue and the question is asked in the Follow-ups strip. Dropped HERE, the
  // one builder the middle and the Ongoing timeline rail share (lib/storyVisibility `isUndrawnStep`).
  const rawTl = (story.timeline || []).filter(e => !isUndrawnStep(e))
  const tl = dedupeSteps([...rawTl.filter(e => !e?.viaOngoingId), ...rawTl.filter(e => e?.viaOngoingId)])
    .sort((a, b) => stepTs(a.createdDate) - stepTs(b.createdDate))

  // RESEARCH PACKS: each pack is one research thread with its OWN mini-timeline (its saves, notes,
  // tasks — every event tagged with its packId). An ongoing can hold several packs; each shows as one
  // expandable node titled by its latest search. Items/events without a packId are legacy data —
  // grouped by query into pseudo-packs so nothing is lost.
  const packs = story.packs || []
  const packIds = new Set(packs.map(p => p.id))
  // kind 'doc' = the pack's own write-up document — internal, never a snippet/count.
  const research = (story.research || []).filter(r => r.kind !== 'doc')
  const inPack = (x) => x.packId && packIds.has(x.packId)
  // Owned by a Research Pack in EITHER of the two ways an item can be: filed into one (`packId`),
  // or shared into some (`packIds`). `inPack` above only knows the first, and it is used for the
  // "Relationship: {pack}" line, which names a single pack and so is right to stay narrow.
  const ownedByAnyPack = (x) => inPack(x) || (x.packIds || []).some(id => packIds.has(id))
  // "Relationship: Prada Sunglasses" — a pack-tagged task/event names its pack on the ongoing story.
  const packTitleOf = (id) => (packs.find(p => p.id === id)?.title || '').trim()

  // 1. Where it started (NOT expandable, always first). Research origin reads as the Figma step:
  // globe icon, "Research", "Started researching {title}." — the story's NAME is the page heading.
  const originStep = isResearchOrigin(story.origin)
    ? {
        key: 'origin',
        svg: 'global', color: moduleColor('research'),
        // "<what was searched> / Research started" — the story's FIRST step, drawn in every research
        // frame (40006448:210248, 40008185:405895, 40008216:411421) above the pack nodes
        // (2026-09-15 parity audit E-5/E-7, busy-my-web#487). It used to read "Research / Started
        // researching Buy sunglasses.", which put the kind on the title line and the subject in a
        // sentence underneath — the reverse of every other step on the rail, where the title is the
        // THING and the line under it is what happened to it. The frames write it the app's own way.
        title: (story.title || '').trim() || 'Research',
        // …except on a sub-ongoing, where the backend has already written the truer sentence:
        // 'Started as a sub-ongoing of "Prada shopping".' (busy-timeline-srv#12). It is the only
        // place the relationship is stated in WORDS — a child made by the endpoint is a `research`
        // origin, so without this the one line that says where it came from was replaced by a
        // generic "Research started" and never appeared anywhere on the page.
        sub: subOriginLine(story) || 'Research started',
        who: 'started a search',
        // …and it STAYS when the pack nodes arrive (see `timelineSteps`): the frames draw it above
        // them, and it is the one row that says what the whole ongoing is about.
        keepWithGroups: true,
        ...stamp(tl.find(e => e.kind === 'search')?.createdDate || story.createdDate || story.updateDate), expandable: false,
      }
    : {
        key: 'origin',
        svg: origin.icon,
        color: origin.color,
        // Don't SAY the type — the icon already shows it. Lead with the story's own name.
        title: (story.title || '').trim() || originHeadline(story), sub: story.story || '',
        who: 'started this ongoing',
        ...stamp(tl.find(e => e.kind === 'search')?.createdDate || story.updateDate), expandable: false,
      }
  // Email-origin: the emails themselves become the timeline (below) — the origin stays a plain
  // "started this ongoing" header and is dropped entirely once there are email nodes to show.

  // The entity title carried in the label ("Note added: X" → "X").
  // Tolerates a missing label: a timeline event without one used to throw here and blank the story.
  const labelTitle = (label) => {
    const s = String(label || '')
    return (s.includes(': ') ? s.slice(s.indexOf(': ') + 2) : s).trim()
  }
  // Display-ready detail: plain text (tags and entities off — see toPlainText) and no first line
  // that just repeats the title.
  const cleanDetail = (e) => {
    const lines = toPlainText(e.detail).split('\n')
    if (lines[0] && lines[0] === labelTitle(e.label)) lines.shift()
    return lines.join('\n')
  }

  // The detail's leading meta lines ("Due tomorrow at 9:00am", "4 checklist items") vs the body.
  const splitDetail = (e) => {
    const lines = cleanDetail(e).split('\n')
    const meta = []
    while (lines.length && (/^Due\b/.test(lines[0]) || /checklist item/.test(lines[0]))) meta.push(lines.shift())
    return { meta: meta.join('\n'), body: lines.join('\n') }
  }

  // What a task/note/event step opens in the SmartBox when clicked in the timeline. Carries the
  // event's identity so saving UPDATES it in place (no duplicate notes/events). Only the BODY is
  // editable content — the meta lines describe the due date/checklist, they aren't the note text.
  // …and a CONTACT is one of them now (busy-my-web#242). Fatemeh made a contact inside an ongoing
  // and "کانتک رو نمیتونم توی یونیورسال اینپوت باکس باز کنم" — it could not be opened. The step was
  // drawn (KIND_STEP has had a contact meta since Stage 13) but carried no opener, so clicking it
  // did nothing at all. It was excluded for having no timeline of its own; since busy-my-web#240 a
  // contact has one (lazily), the box seeds its fields from the record and "Update contact" PATCHes
  // it in place — every reason the exclusion rested on is gone.
  const editOf = (e) => {
    const menu = { task: 'tasks', note: 'notes', event: 'events', contact: 'contacts' }[e.kind]
    if (!menu) return undefined
    // e.entityId = the REAL note/task/event — with it the box PATCHes the entity itself (star,
    // due date, checklist all persist); without it (older events) only the story event is editable.
    return { menu, id: e.entityId || null, title: labelTitle(e.label), content: splitDetail(e).body, ongoingId: story.id, eventId: e.id, dueDate: e.dueDate || '' }
  }

  // One row of a pack's mini-timeline: the exact search for saves, the entity for notes/tasks.
  // A mini carries `_ts` like every other step: the zoomed rail (GlobalTimelinePanel, ?pack=) mixes
  // these rows back in with the pack's nodes and its tasks, and a row with no instant can only be
  // placed by the order it was assembled in — which is a grouping, not a chronology.
  const miniStep = (e, key) => {
    if (e.kind === 'save' || e.kind === 'research') {
      const title = /^your search/i.test(e.label) ? e.label : `Your search: ${e.label}`
      // The search text leads the row; "Web search" is the kind under it.
      return { _ts: stepTs(e.createdDate), key, svg: 'global', color: moduleColor('research'), title, type: typeTitle('research'), detail: '', ...stamp(e.createdDate) }
    }
    const meta = KIND_STEP[e.kind] || { svg: 'notes', color: '#9ca3af' }
    return { _ts: stepTs(e.createdDate), key, svg: meta.svg, color: meta.color, title: labelTitle(e.label), type: kindWord(e.kind), detail: cleanDetail(e), ...stamp(e.createdDate), edit: editOf(e) }
  }

  const packNode = (key, title, ts, when, items, events, packId = null) => {
    const group = emptyResearchGroup(title)
    // The node COUNTS what was ticked into the pack (`isPackNodeItem`, busy-my-web#485): the
    // comparison saved into it is a story step of its own and stays off the "3 photos 2 snippets"
    // line — but it is still put on the group AFTER the summary is taken, because the pack's own
    // page reads `group.comps` to list and open it (#153, Saffet: "also in each research pack the
    // comparison"). Counted nowhere, findable everywhere.
    for (const r of items.filter(isPackNodeItem)) fileResearchItem(group, r)
    const summary = stepSummary(group)
    for (const r of items) if (r.kind === 'comparison') group.comps.push(r)
    // Build the pack timeline: the pack is ONE research — however many searches/saves it took, they
    // consolidate into a single "Saved …" row whose time is the LATEST save. (The individual
    // searches are listed on the pack's own page.) Notes/tasks keep their own rows.
    // No "Saved research" row inside the pack (the node itself already says what was saved —
    // opening it shows the items). Only the pack's notes/tasks keep their own mini rows.
    const packSteps = []
    events.forEach((e, j) => {
      if (e.kind === 'save' || e.kind === 'research') return
      // Tasks/events show ONCE — as chronological top-level steps, not again inside the pack.
      if (e.kind === 'task' || e.kind === 'event') return
      packSteps.push(miniStep(e, `${key}:m${j}`))
    })
    const stepCount = packSteps.length
    return {
      _ts: ts,
      key,
      stepKind: 'research',
      progress: true, // MVP: pack creations ARE the ongoing story
      packId, // real packs can be CONTINUED (new items land in them); null for legacy pseudo-packs
      // Figma step: globe icon, the pack's NAME as the headline, then "Research Pack created" on
      // its own medium line, then the contents summary. (Name first — the pack is what you look for;
      // created/updated is when.)
      svg: 'global', color: moduleColor('research'),
      title: title || 'Research',
      subTitle: 'Research Pack created',
      // A pack saved ONCE never reaches the per-save loop below, so it used to carry the "created"
      // WORDS without the fact — anything keying off `packPhase` (rather than off the sentence)
      // could not see a brand-new pack as created at all. The multi-save loop overwrites both.
      packPhase: 'created',
      latestForPack: true,
      // The counted line on its own — the multi-save loop below re-words it ("… added") and must
      // not re-derive it from a group the pack page's comparisons have since been put on.
      summary,
      sub: [summary, stepCount > 1 && `${stepCount} steps`].filter(Boolean).join(' · '),
      who: 'researched',
      ...when,
      group,
      packSteps,
      // Saved EMAILS count here too (busy-timeline-srv#35). `stepSummary` above already writes
      // "1 email" onto the node, and a row that states what is inside it and then refuses to open
      // is worse than one that says nothing — a pack holding nothing but mail is a pack with
      // research in it.
      expandable: stepCount > 0
        || (group.photos.length + group.links.length + group.notes.length + (group.emails || []).length
          + (group.videos || []).length) > 0,
    }
  }

  // 2. Everything else, collected with a sortable timestamp so sub-stories slot in by creation time.
  const middle = []
  tl.forEach((e, i) => {
    if (['search', 'selection'].includes(e.kind)) return // start is the origin; selection is noise
    // A pack-tagged TASK/EVENT belongs to the Ongoing for progress AND stores its pack relationship
    // (MVP Stage 8): it surfaces as a top-level story step with a "Relationship: {pack}" line, while
    // everything else pack-tagged (notes, saves, comparisons) stays inside the pack.
    const relationship = inPack(e) ? packTitleOf(e.packId) : ''
    // A SHARED event — a comparison saved into SEVERAL packs (packIds via its shared item, no
    // single packId) — is real progress on the ongoing story and names EVERY pack as a chip
    // (frames 13-14); two packs or more, the list is whatever was compared.
    const sharedPackIds = !e.packId ? (e.packIds || []).filter(id => packIds.has(id)) : []
    const shared = sharedPackIds.length > 1
    // Pack-tagged notes/contacts/comparisons normally live INSIDE the pack — but they still become
    // filter-only top-level steps, so "Show: Notes" in the Context Panel can list them.
    const packOnly = inPack(e) && e.kind !== 'task' && e.kind !== 'event'
    if (e.kind === 'save' || e.kind === 'research') return // legacy saves → pseudo-packs below
    // A FILE attached to the ongoing or to one of its entities (doc-attach, 2026-10-02): the file's
    // name over "Attachment added", the parent's title under it, opened through the download route.
    // Its own builder (lib/attachmentRows.js) — it is not an entity step and has nothing to edit.
    if (e.kind === 'doc') {
      const doc = storyDocStep(e)
      if (doc) middle.push({ ...doc, _ts: stepTs(e.createdDate), via: viaTag(e) || undefined })
      return
    }
    const ts = stepTs(e.createdDate)
    const meta = KIND_STEP[e.kind]
    if (!meta) return
    // Figma Stage 7 shape: the VERB is the step title ("Task created"), the entity's own name is
    // the medium sub-line, and the leading meta lines of the detail ("Due tomorrow at 9:00am",
    // "4 checklist items") show under it; the body stays in `detail` for the expanded view.
    // A timeline event with no label at all must not take the whole story down (it used to throw
    // "Cannot read properties of undefined" here and blanked the page) — treat it as an empty label.
    const label = String(e.label || '')
    const sep = label.indexOf(': ')
    const verb = sep > 0 ? label.slice(0, sep).trim() : label.trim()
    const subTitle = sep > 0 ? labelTitle(e.label) : ''
    // …and what the RAIL calls it, where the frames word it differently. Frame 40008216:411421
    // draws the very same comparison twice: "Prada vs Louis Vuitton / Comparison created" in the
    // middle, and "Prada vs Louis Vuitton / Comparison saved" on the Ongoing Timeline beside it —
    // with the packs it went into listed under it and its PDF, when one was made, as a chip
    // (2026-09-15 parity audit E-7, busy-my-web#488). The middle records that a comparison came
    // into existence; the rail records where it was PUT, which is the fact the rows under it are
    // about. Undefined everywhere else, so the rail keeps reading `subTitle` for every other step.
    const railSubTitle = e.kind === 'comparison' ? 'Comparison saved' : undefined
    let { meta: metaLines, body } = splitDetail(e)
    // A contact's detail (email / phone count / company) IS its summary — show it inline
    // (Figma Stage 13 "Contact created / Pioneer Pet / 5 texts added"), nothing to expand.
    // Same for the "Ongoing created" step of a story built from email groups (busy-my-web#104):
    // "Created from 2 connected email groups" IS the step, and frame 40007198:182698 draws it on
    // the line under the verb rather than behind an expander.
    // …and for the sub-ongoing receipt (busy-timeline-srv#12): "Created from this ongoing." IS the
    // step. A chevron opening onto that one sentence would be a door onto a corridor.
    if (INLINE_DETAIL_KINDS.has(e.kind) && body) {
      metaLines = [metaLines, body].filter(Boolean).join('\n')
      body = ''
    }
    // The child this receipt announces, when the ongoing list has it (it always does once the list
    // has been read — the child is a row in it). Matched on the NAME, because the receipt carries no
    // EntityId by design: `EntityId` means "the note/task/event this step edits", and an ongoing id
    // there would send a reader hunting for an entity that does not exist (busy-timeline-srv#12's
    // own note). With the child resolved the step becomes the sub-ongoing's node on the rail: it
    // navigates like one (`subId`) and it is a `sub` for the kind filter, so the Briefing's
    // "N sub-ongoings" row still lists it and the branch elbow is still drawn beside it.
    const receiptName = subReceiptName(e)
    const receiptChild = receiptName ? (children || []).find(c => subStoryLabel(c) === receiptName) : null
    // The due line is built LIVE from the event's dueDate, so its wording updates by itself:
    // "Due tomorrow at 9:00am" → (after the date passes) "Overdue — was due yesterday at 9:00am".
    // Older events carry a static "Due …" text line instead — kept as-is when there's no dueDate.
    let dueLine = ''
    let overdue = false
    if (e.dueDate) {
      // …except an EVENT, which has no "was due" at all (busy-my-web#238, Saffet: "after it passes
      // it has passed"). It reads its own lifecycle label instead — "Tomorrow, 9am", "In 1h 38m",
      // "Now", "Ended 38m ago", "Past · 26 Aug, 9am" — the SAME sentence the global feed and the
      // side panel put on the same event (lib/eventTime.js), and it never turns the line red.
      if (e.kind === 'event') {
        dueLine = eventLabel(e.dueDate, { end: e.dueEnd || null })
        overdue = false
      } else {
        overdue = parseUtc(e.dueDate).getTime() < Date.now()
        dueLine = overdue ? `Overdue — was due ${friendlyDue(e.dueDate)}` : `Due ${friendlyDue(e.dueDate)}`
      }
      metaLines = metaLines.split('\n').filter(l => !/^Due\b/.test(l)).join('\n') // no double due line
    }
    middle.push({
      _ts: ts, key: `event:${i}`, ...meta,
      // Where this step sits among steps stamped with the SAME instant (#153). A shared comparison
      // is saved INTO the packs it names — the server writes both from one `now` — so it belongs
      // after them. Everything else keeps the default 0 and the stable order it was built in.
      _ord: shared ? 1 : 0,
      // What belongs on the Ongoing Story's middle timeline: anything created ON the ongoing.
      //
      // It used to be tasks and events only (plus a shared comparison), on the reasoning that
      // notes/contacts/comparisons "live inside their Research Pack". That is true of the ones
      // filed into a pack — and those are still excluded, by `inPack` — but a note added to the
      // ongoing itself belongs to nothing else, so excluding it meant the user could create a note
      // and then not find it anywhere in the middle. Pouria: "in the ongoing story in the middle I
      // can't see the note; we're supposed to see those things created in the ongoing in the middle
      // as timeline."
      //
      // Tasks and events stay unconditional: pack-tagged or not, they are the ongoing's work.
      progress: e.kind === 'task' || e.kind === 'event' || shared || !ownedByAnyPack(e),
      filterOnly: packOnly || undefined,
      stepKind: receiptName ? 'sub' : e.kind,
      subId: receiptChild?.id || undefined,
      relPackId: e.packId || undefined,
      relationship: relationship || undefined,
      // A step borrowed from a SUB-ONGOING (busy-timeline-srv#27, spec §5): the parent's story shows
      // its descendants' activity, and the relationship travels with it — the step says which child
      // it happened in and can open that child. Undefined on a story's own steps, which is all a
      // child ever has: the server's walk only goes downwards.
      via: viaTag(e) || undefined,
      // Every pack of a shared comparison — rendered as chips, each opening its pack.
      relPacks: shared ? sharedPackIds.map(id => ({ id, title: packTitleOf(id) })) : undefined,
      // WHICH comparison this step is, when it is one (busy-my-web#506). The comparison's own
      // research item is the key its PDF is filed under, so the rail can ask "has this comparison
      // got a PDF" and draw the chip frames 40006859:277503 / 40008216:411421 put under
      // "Comparison saved". Undefined on every other kind, so nothing else asks.
      comparisonId: e.kind === 'comparison' ? (e.entityId || undefined) : undefined,
      // NAME first, then the verb — the same order the pack nodes above already use, and what
      // frame 40007883:371211 draws: "Visit Prada store" as the headline, "Task created" on the
      // medium line under it, then the due line. It used to be the other way round for these
      // steps, so a list of tasks read as a column of "Task created" with the actual task
      // demoted underneath.
      // With no name in the label there is nothing to promote, so the verb stays the headline.
      title: subTitle || verb,
      subTitle: subTitle ? verb : undefined,
      railSubTitle,
      dueLine: dueLine || undefined,
      // The raw instant as well as the sentence: the side panel gives a dated step the same status
      // treatment as a global-feed row (bell + how far over / how long is left), which it computes
      // against a ticking clock — it needs the date itself, not the wording.
      dueDate: e.dueDate || undefined,
      overdue,
      sub: metaLines,
      detail: body,
      // Expandable on a body OR a date (frame 40007893:378129 expands a task to its "Due" group and
      // its files): the panel's expansion is where a step's own details live, and a dated task with
      // no written body had none of them to open. Not on the files, though — the step cannot know
      // whether the entity has any without resolving it, and a chevron that opens onto nothing is
      // worse than one that never appeared.
      // The email groups an email-born story was created FROM (busy-my-web#499): they ride on the
      // created step itself, so the ONE step says where the ongoing came from in full.
      groupRows: e.kind === 'email' ? createdGroupRows(createdStepGroups(e, story)) : undefined,
      ...stamp(e.createdDate), edit: editOf(e), expandable: !!body || !!e.dueDate,
    })
  })

  // The ongoing's linked threads — each a node on the timeline (received / sent / forwarded), placed
  // in time order exactly like research items. So opening an ongoing shows a TIMELINE of its emails,
  // not a flat list. Each email node opens its full thread (two-click) and expands in the side panel
  // with the sender, preview and real attachment thumbnails.
  //
  // Not every linked thread is mail: the thread carrying a task, a note or an event is linked too, and
  // it used to be drawn here as an email — so a brand-new task's ongoing opened with an email
  // "attached" to it that nobody had attached (Pouria, 2026-08-26). `storyThreadNodes` decides which
  // threads still deserve a node (one the story already has a STEP for does not), and `threadKind`
  // says what each one is, so a task thread is drawn as a task and never opens a mail thread.
  //
  // An entity thread is a full entity step: it carries the SAME `edit` payload `editOf` builds, keyed
  // by the entity's own id, so clicking the one task a fresh ongoing is made of opens that task
  // exactly as clicking it in any other story does (Pouria's follow-up the same day).
  const emailMeta = originMeta('email')
  const THREAD_META = {
    task: KIND_STEP.task,
    note: KIND_STEP.note,
    event: KIND_STEP.event,
    file: { svg: 'file', color: moduleColor('research') },
  }
  // A MESSAGE thread (busy-timeline-srv#29) — an SMS/WhatsApp conversation or a logged call — is a
  // node like an email's: real traffic on the story, titled after the people on it. Its glyph is
  // the messaging module's, or the phone for a call (lib/sources.js reads which off the subject).
  const messageMetaOf = (em) => {
    const meta = messageMeta(threadSource(em))
    return { svg: meta.icon, color: meta.color, label: meta.label }
  }
  // The box's opener for an entity thread — `editOf`'s twin for a thread that has no story event:
  // the entity's OWN id, which is the "standalone entity" form SmartBox's onEditEntity PATCHes in
  // place (it never sets both an id and an eventId).
  const threadEdit = (em, kind) => {
    if (!threadOpensEntity(em)) return undefined
    const menu = { task: 'tasks', note: 'notes', event: 'events' }[kind]
    return { menu, id: em.entityId, title: em.subject || '', content: '', ongoingId: story.id, dueDate: em.dueDate || '' }
  }
  // DECISION TAKEN 2026-09-15 (busy-my-web#499): an EMAIL-BORN ongoing draws ONE created step with
  // its groups under it — not one "Email received" node per message. So where the story's own
  // timeline already carries that created step, the mail it was created from is not repeated as
  // nodes. Threads that are not mail (the task/note/event a thread carries) still get theirs, and a
  // story of any other origin is untouched: an email CONNECTED to a task-born ongoing is real
  // traffic on it and keeps its node.
  const emailBorn = String(story.origin || '').toLowerCase() === 'email'
    && tl.some(e => e?.kind === 'email')
  storyThreadNodes(story).forEach((em) => {
    const kind = threadKind(em)
    const isEmail = kind === 'email'
    if (isEmail && emailBorn) return
    const isMessage = kind === 'message'
    const messageMetaFor = isMessage ? messageMetaOf(em) : null
    const meta = isEmail ? { svg: emailMeta.icon, color: emailMeta.color } : (messageMetaFor || THREAD_META[kind])
    // The same live due wording an entity step gets, so a dated task reads the same on both rails.
    const overdue = !!em.dueDate && parseUtc(em.dueDate).getTime() < Date.now()
    const dueLine = em.dueDate
      ? (overdue ? `Overdue — was due ${friendlyDue(em.dueDate)}` : `Due ${friendlyDue(em.dueDate)}`)
      : ''
    const edit = threadEdit(em, kind)
    middle.push({
      _ts: stepTs(em.receivedUtc),
      // Mail keeps the `email:<timelineId>` key other surfaces already focus by name (the connections
      // panel's "View"); an entity thread takes its own namespace so it can never collide with the
      // `event:<index>` keys the story's own steps use.
      key: isEmail ? `email:${em.timelineId}` : `thread:${kind}:${em.timelineId}`,
      progress: true,
      stepKind: kind,
      svg: meta.svg,
      color: meta.color,
      title: em.subject || (isEmail ? 'Email' : isMessage ? messageMetaFor.label : typeTitle(kind) || 'Item'),
      // A message node says what it is under its title ("Message" / "Call") the way an entity node
      // does — there is no mail reader behind it to make that obvious.
      subTitle: isEmail ? undefined : isMessage ? messageMetaFor.label : kindWord(kind) || undefined,
      dueLine: dueLine || undefined,
      dueDate: em.dueDate || undefined,
      overdue,
      sub: '',
      who: '',
      // Only a real mail thread carries the email affordances — with these set, a task node opened
      // the email reader on a thread that holds no mail.
      emailTimelineId: isEmail ? em.timelineId : undefined,
      emailFrom: isEmail ? (em.from || '') : '',
      emailPreview: isEmail ? (em.preview || '') : '',
      emailAttachments: isEmail ? (em.attachments || []) : [],
      ...stamp(em.receivedUtc),
      edit,
      // …and WHERE to look for that entity when the payload did not name it (busy-my-web#267).
      // `threadEdit` needs an `entityId`; a thread that arrives without one — an older story, a
      // payload whose items were not classified — left the node with no opener at all, and an event
      // node also has nothing to expand (its thread carries no `dueDate`, the story's own due field
      // being an email notion). The result was a row drawn as an event that answers no click
      // whatsoever: "no matter how much I click on the event it doesn't open" (Fatemeh, #267).
      // So the node says which KIND it is and which THREAD holds it, and the rail resolves the
      // entity off that thread when it is asked to open it — the same timeline record it already
      // reads to expand an email node. Only for a node that has no opener of its own: with an
      // `entityId` in hand nothing here is needed.
      entityThreadKind: !isEmail && !edit ? kind : undefined,
      entityThreadId: !isEmail && !edit ? em.timelineId : undefined,
      // A mail node expands onto its thread; an entity node onto its due details, the same rule the
      // entity steps follow (a chevron over nothing is worse than no chevron).
      expandable: isEmail || !!em.dueDate,
    })
  })

  // Real packs. A pack saved ONCE is one "Research Pack created" node. A pack saved AGAIN later
  // splits per save: "created" keeps its original place with the items of the FIRST save, and each
  // later save becomes a "Research Pack updated" node at ITS chronological spot holding what that
  // save added. The middle shows only the LATEST node (re-aligned + retitled); the side panel
  // shows all of them (created stays, updated appended after whatever came between).
  packs.forEach((p) => {
    // Membership includes SHARED items/events (a comparison saved into several packs lives in all).
    const items = research.filter(r => itemInPack(r, p.id))
    const events = tl.filter(e => itemInPack(e, p.id))
    const saves = events
      .filter(e => e.kind === 'save' || e.kind === 'research')
      .sort((a, b) => stepTs(a.createdDate) - stepTs(b.createdDate))
    if (saves.length <= 1) {
      // A one-save pack sits at the moment its CONTENTS were last written — its own save event —
      // and it PRINTS that same instant, which is the rule issue #113 settled: on a rail that is a
      // time axis, a step is where its stamp says it is. (#113 reached for `updateDate` to get
      // there, because a one-save pack's save and its update were the same moment.)
      //
      // They are not the same moment any more. `OngoingService.Advance` bumps `pack.UpdateDate` for
      // EVERY step tagged with the pack — a task raised inside it, a comparison saved into it, the
      // write-up document auto-saving — not just for research. So the pack's node slid down the
      // rail to the instant of whatever last touched it, landing on (or under) the very task the
      // user had just created inside it: "the task I made after updating the pack shows above the
      // pack". The save event is the fact this node states ("Research Pack created", these items),
      // so that is where it belongs and what it says. `updateDate`/`createdDate` remain the
      // fallback for a pack with no save event of its own (legacy rows).
      const anchor = saves[0]?.createdDate || p.updateDate || p.createdDate
      middle.push(packNode(`pack:${p.id}`, p.title, stepTs(anchor), stamp(anchor), items, events, p.id))
      return
    }
    // Assign each item to the save it arrived with (SavedDate vs the save event's time; the
    // backend stamps both with the same clock, so a small epsilon covers request skew).
    const eps = 5000
    const saveTs = saves.map(s => stepTs(s.createdDate))
    const windows = saves.map(() => [])
    // Only node items are bucketed (busy-my-web#485): a comparison saved after the last save
    // used to fall into the "updated" window and print "+1 comparison added" on a save that added
    // no such thing. It joins the latest node's contents below instead, uncounted.
    items.filter(isPackNodeItem).forEach((r) => {
      const t = stepTs(r.savedDate)
      let k = saveTs.findIndex(ts => t <= ts + eps)
      if (k === -1) k = saves.length - 1
      windows[k].push(r)
    })
    const comps = items.filter(r => r.kind === 'comparison')
    // The pack's FULL contents — the middle's summary line and the focused pack page use these.
    const fullGroup = emptyResearchGroup(p.title)
    items.forEach((r) => fileResearchItem(fullGroup, r))
    // Running totals across the pack's saves — an "updated" node states BOTH what that save added
    // and where the pack stands afterwards (frames 40007749:349789 and 40007883 S1:
    // "+2 photos · 5 photos. total · 2 snippets"), which needs the counts of every earlier window
    // as well as its own. The saves are already in chronological order, so one pass is enough.
    let photosSoFar = 0
    let snippetsSoFar = 0
    saves.forEach((sv, k) => {
      const isLast = k === saves.length - 1
      const node = packNode(
        `pack:${p.id}:${k}`, p.title,
        saveTs[k], stamp(sv.createdDate),
        isLast ? [...windows[k], ...comps] : windows[k],
        isLast ? events : [sv],
        isLast ? p.id : null, // only the latest node IS the pack (focus/continue target)
      )
      // Name stays the headline; created/updated is the secondary line.
      node.subTitle = k === 0 ? 'Research Pack created' : 'Research Pack updated'
      node.packPhase = k === 0 ? 'created' : 'updated'
      node.latestForPack = isLast
      photosSoFar += node.group.photos.length
      snippetsSoFar += node.group.notes.length
      // An "updated" node describes what THAT save added. Both panes draw it as one plain grey
      // line ("+2 photos  5 photos total  2 Extractions" — busy-my-web#594, Pouria's design
      // 2026-10-07), so the numbers travel as data rather than as a sentence the template would
      // have to take apart again; `sub` stays as the sentence form (the middle hides it when a
      // delta is present).
      if (k > 0) {
        // BOTH kinds carry both numbers (busy-my-web#181). Snippets used to travel as a running
        // total only, so a save that added two snippets and nothing else had no delta to state —
        // the node said "2 snippets" whether they arrived now or three saves ago.
        // `lib/packDeltaChips`' `packDeltaLine` turns the four numbers into the line's segments.
        // History: #534 (Saffet, 2026-09-17) stopped showing these running totals in favour of one
        // "N items. total" chip counted off the node's own dropdown; #594 (2026-10-07) SUPERSEDES
        // that — the totals here are the pack's running totals across saves, while the dropdown
        // lists this save's items, so the two may differ. That is what the design shows.
        node.delta = {
          photosAdded: node.group.photos.length,
          photosTotal: photosSoFar,
          snippetsAdded: node.group.notes.length,
          snippetsTotal: snippetsSoFar,
        }
        const added = node.summary
        node.sub = added ? `${added} added` : 'Updated'
      }
      if (isLast) {
        node.fullGroup = fullGroup // the focused pack page shows the WHOLE pack
        // The middle shows THIS node (latest info) but sorted at the pack's CREATED time — an
        // update never moves the pack down the story.
        node.middleTs = stepTs(p.createdDate) || saveTs[0]
      }
      middle.push(node)
    })
  })

  // Legacy research (saved before packs existed): group by query into pseudo-packs.
  const legacyGroups = legacyPackGroups(story)
  const legacySaves = tl.filter(e => (e.kind === 'save' || e.kind === 'research') && !inPack(e))
  legacyGroups.forEach((g, i) => {
    const ev = legacySaves[i]
    const ts = ev ? stepTs(ev.createdDate) : 0
    const items = [...g.photos, ...g.links, ...g.notes]
    middle.push(packNode(`pack-x:${i}`, g.query === 'Saved research' ? (story.title || 'Your research') : g.query, ts, stamp(ev?.createdDate || story.updateDate), items, ev ? [ev] : []))
  })

  // Sub-stories: each is its own node in the timeline, in creation order. The icon is what the sub
  // STARTED from (note-started sub → note icon, research → globe, …); the branch line/indentation in
  // the views marks it as a sub-story — no type text needed.
  //
  // …unless the story ALREADY says so. A sub-ongoing made through busy-timeline-srv#12's endpoint
  // leaves a permanent "Sub-ongoing created: <name>" step on this parent, and that step is now the
  // child's node (it navigates, it filters as a `sub`, it is drawn with the branch elbow). Weaving
  // the child in a second time from the ongoing list would print the same event twice, a
  // millisecond apart. The receipt wins because it is part of the story the backend wrote.
  const announced = new Set(tl.map(subReceiptName).filter(Boolean))
  ;(children || []).forEach((c) => {
    if (announced.has(subStoryLabel(c))) return
    const ts = stepTs(c.createdDate || c.updateDate) || Number.MAX_SAFE_INTEGER
    const subOrigin = originMeta(c.origin)
    middle.push({
      _ts: ts,
      key: `sub:${c.id}`,
      progress: true,
      stepKind: 'sub',
      svg: subOrigin.icon, color: subOrigin.color,
      title: subStoryLabel(c),
      sub: '',
      who: 'started a sub-story',
      ...stamp(c.createdDate || c.updateDate),
      subId: c.id,
      expandable: true,
    })
  })

  // THE STORY RAIL'S DIRECTION (issues #113 / #108) — the same one rule the Global timeline follows,
  // read on a surface that faces the other way. Both rails end at NOW: the bottom row is the thing
  // closest to the present moment, and going UP walks away from it. The global feed looks FORWARD
  // (deadlines), so its far end is the future and that is what sits at its top; a story is a RECORD
  // of what has already happened, so its far end is the past — oldest at the top, most recent at the
  // bottom, which is what Saffet asked for: a task created after a save reads after (below) it.
  // Ascending by the step's own instant, then, and — because Array#sort is stable — two steps made
  // in the same millisecond keep the order they were built in.
  //
  // …which was not good enough for a saved comparison (#153, Saffet: "I did a comparison and it
  // saved it BEFORE the two of the research packs — it should be after"). busy-timeline-srv's
  // SaveComparison takes ONE `now` and stamps three things with it: the comparison's timeline
  // event, and `UpdateDate` on every pack it was saved into. A one-save pack node sits at its
  // `updateDate`, so the comparison and the two packs it compares land on the exact same
  // millisecond — and "keep the order they were built in" then meant the order of this function's
  // own pushes, which collects timeline events (step 2) before pack nodes (step 4). The rail
  // printed the comparison above the packs that produced it.
  //
  // So the tie-break is made to MEAN something instead of being an accident of construction: at the
  // same instant, an effect follows its causes. A comparison is saved INTO the packs it names, so
  // it can only have happened after them, whatever a shared clock says.
  middle.sort((a, b) => (a._ts - b._ts) || ((a._ord || 0) - (b._ord || 0)))

  // No trailing "Thinking…" step (Figma Stage 5): the story ends on the real work. What to do
  // next lives in Follow-ups / Focus, not as a fake timeline node.

  // Research origin keeps its "Research / Started researching …" header ABOVE the pack nodes
  // (Figma shows both). Email-origin still drops the redundant header once email nodes exist.
  // …or its own "Ongoing created" step (busy-my-web#104): a story built from email groups says
  // where it came from on that step already, so the generic "Started from your email" header above
  // it would be the same sentence twice — which is what frame 40007198:182698 shows it is not.
  const hasEmailStep = middle.some(s => s.emailTimelineId || s.stepKind === 'email')
  if (String(story.origin || '').toLowerCase() === 'email' && hasEmailStep) {
    return middle
  }

  // The same rule for a story STARTED FROM an entity (task / note / event): once that very entity is
  // on the rail as its own step, the header above it is the same thing said twice — and the header is
  // the one that cannot be opened. A task-origin story therefore opens on its task, which is what
  // gets clicked (Pouria, 2026-08-26: "I can't open the task — it should be same as the other").
  const originKind = String(story.origin || '').toLowerCase()
  const originEntityOnRail = ENTITY_STEP_KINDS.includes(originKind)
    && middle.some(s => String(s.key).startsWith(`thread:${originKind}:`) && s.edit)
  if (originEntityOnRail) {
    return middle
  }

  // A story STARTED FROM an entity that is not itself on the rail — a task created on an open email,
  // whose ongoing took the email thread as its first member (2026-08-29) — keeps its origin step,
  // but IN TIME ORDER with the rest: the thread's messages came before the task that was made
  // about them, so the rail reads emails first, then the task (Pouria: "the task should be after
  // the email"). Pinning the header first put the newest thing on top of a record that runs
  // oldest → newest everywhere else.
  if (ENTITY_STEP_KINDS.includes(originKind) && middle.length) {
    originStep._ts = stepTs(story.createdDate || story.updateDate)
    originStep.progress = true
    Object.assign(originStep, stamp(story.createdDate || story.updateDate))
    middle.push(originStep)
    middle.sort((a, b) => (a._ts - b._ts) || ((a._ord || 0) - (b._ord || 0)))
    return middle
  }

  // MVP Ongoing Story: once a Research Pack node exists, the "Research / Started researching…"
  // header is redundant in the MIDDLE (the pack nodes carry the story) — the side panel keeps it.
  const hasPackNode = middle.some(x => String(x.key).startsWith('pack'))
  originStep.progress = !(isResearchOrigin(story.origin) && hasPackNode)

  return [originStep, ...middle]
}

// ── A story's email threads, laid flat (Pouria, 2026-08-29) ─────────────────────────────────────
// "We don't need the first one and the last one — two emails, one task, and the same in the story."
// A thread that is a member of the story is not ONE node with the conversation folded inside it and
// the task that was made about it repeated as the story's own origin step: the thread's rows ARE
// the story — each message at its own time, each task / note / event on the thread at the moment it
// was put there — and the origin step that merely names that same task steps aside. Both panes
// read this one rule, so they cannot drift apart.
//
// `getTimeline(id)` is the store's `timeline/getTimelineById`; a thread not loaded yet keeps its
// node (the panes prefetch it and re-run).
// Who a message is from / to, read the estate's one way (lib/senderDisplay.js, busy-my-web#361):
// the display name where the header carries one, the bare address where it does not. The local
// copies of this parse are gone — the step and the mailbox row must never name the same mail
// differently.
const emailAddrOf = (raw) => senderAddress(raw).toLowerCase()
const nameOf = (raw) => senderDisplayName(raw)

export function flattenThreadSteps(steps, getTimeline, { story = null, selfEmail = '' } = {}) {
  const list = Array.isArray(steps) ? steps : []
  if (typeof getTimeline !== 'function') return list
  const me = String(selfEmail || '').toLowerCase()
  const out = []
  const flattenedEntities = [] // { kind, title } — what the origin step may be a copy of
  let flattenedAny = false
  // The documents the story already has a `"doc"` step for (doc-attach, 2026-10-02): a file on a
  // task's thread is ALSO announced by timeline-srv as a story step, and one file is one row.
  const docSteps = new Set(list.filter(s => s?.docId).map(s => String(s.docId)))
  list.forEach((step) => {
    const tid = step?.emailTimelineId
    const tl = tid ? getTimeline(tid) : null
    if (!tid || !tl) { out.push(step); return }
    const rows = []
    ;(tl.timelineItems || []).forEach((it) => {
      if (!it || it.isDeleted) return
      if (it.itemType === 1) {
        const m = (tl.emailMessages || []).find(e => e.id === it.id)
        if (!m || m.isDeleted === true) return
        const sent = !!me && emailAddrOf(m.senders) === me
        const who = sent
          ? `To ${nameOf(m.recipientsTo) || emailAddrOf(m.recipientsTo) || 'recipient'}`
          : (nameOf(m.senders) || emailAddrOf(m.senders) || 'Sender')
        // Drawn exactly like the Email timeline's row (frame 40000878:54621, Pouria 2026-08-29):
        // "Email received" / "Email sent" over the subject, then "Sender › To you", the sms glyph
        // in the neutral disc. `flatEmail` is what the panes read to open the MAIL (the reader),
        // not the thread's timeline, and to draw the "Open ↗" line every other step has.
        rows.push({
          _ts: stepTs(m.receivedUtc || it.assignDate),
          key: `email:${tid}:m:${m.id}`,
          progress: true,
          stepKind: 'email',
          flatEmail: true,
          messageId: m.id,
          svg: 'sms',
          color: '#111827',
          title: sent ? 'Email sent' : 'Email received',
          subTitle: m.subject || step.title || '',
          sub: sent ? `You › ${who}` : `${who} › To you`,
          who: '',
          emailTimelineId: tid,
          emailFrom: '',
          emailPreview: m.previewText || '',
          emailAttachments: m.attachments || [],
          ...stamp(m.receivedUtc || it.assignDate),
          expandable: false,
        })
        return
      }
      // A FILE on the thread (itemType 5) — a document attached to the task / note it carries — is
      // a row of its own now (doc-attach, 2026-10-02); an email message's own attachments are not
      // (`threadFileRow` leaves them under their message), nor a file the story already has a step for.
      const file = threadFileRow(it, tl)
      if (file) {
        if (docSteps.has(file.docId)) return
        rows.push({
          _ts: stepTs(file.when),
          key: `thread:file:${tid}:${file.docId}`,
          progress: true,
          stepKind: 'doc',
          svg: file.icon,
          color: file.color,
          title: file.name,
          subTitle: file.type,
          sub: '',
          who: '',
          docId: file.docId,
          docName: file.name,
          ...stamp(file.when),
          expandable: false,
        })
        return
      }
      const kind = { 2: 'task', 3: 'note', 4: 'note' }[it.itemType] === undefined ? null : ({ 2: 'task', 3: 'event', 4: 'note' })[it.itemType]
      if (!kind) return
      const list3 = kind === 'task' ? (tl.tasks || []) : kind === 'note' ? (tl.notes || []) : (tl.events || [])
      const d = list3.find(x => x.id === it.id)
      const title = (it.title || d?.title || d?.summary || typeTitle(kind) || kind).trim()
      const when = it.assignDate || d?.createDate || d?.createdDate
      const meta = KIND_STEP[kind]
      const menu = { task: 'tasks', note: 'notes', event: 'events' }[kind]
      rows.push({
        _ts: stepTs(when),
        key: `thread:${kind}:${tid}:${it.id}`,
        progress: true,
        stepKind: kind,
        svg: meta.svg,
        color: meta.color,
        title,
        sub: '',
        who: '',
        dueDate: d?.dueDate || undefined,
        ...stamp(when),
        edit: { menu, id: it.id, title, content: '', ongoingId: story?.id || null, dueDate: d?.dueDate || '' },
        expandable: false,
      })
      flattenedEntities.push({ kind, title: title.toLowerCase() })
    })
    if (!rows.length) { out.push(step); return }
    flattenedAny = true
    out.push(...rows)
  })

  if (!flattenedAny) return list
  // The origin step that only repeats a task / note / event now on the rail in its own right.
  const originKind = String(story?.origin || '').toLowerCase()
  const originTitle = String(story?.title || '').trim().toLowerCase()
  const originDuplicated = ENTITY_STEP_KINDS.includes(originKind)
    && flattenedEntities.some(e => e.kind === originKind && e.title === originTitle)
  const kept = originDuplicated ? out.filter(s => s.key !== 'origin') : out
  return kept.slice().sort((a, b) => ((a._ts ?? 0) - (b._ts ?? 0)) || ((a._ord || 0) - (b._ord || 0)))
}
